import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, ChevronRight, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const FAQ = {
  es: [
    { q: "¿Cómo cancelo mi suscripción?", a: "Puedes cancelar cuando quieras. Ve a la página Premium y pulsa \"Gestionar suscripción\" para abrir el portal seguro de Stripe, donde cancelas con un clic. Mantendrás el acceso Premium hasta el final del periodo que ya pagaste." },
    { q: "¿Qué incluye Premium?", a: "Evidencia y estudios completos de PubMed, investigación por condición de salud, dosis recomendadas por expertos, la Fórmula del Superatleta y el análisis combinado de Mi Stack (carga en riñón/hígado e interacciones)." },
    { q: "¿Cuánto cuesta?", a: "Premium cuesta $9.99 al mes o $59.99 al año (ahorras ~50% con el plan anual). Puedes cancelar cuando quieras." },
    { q: "¿Cuántos escaneos gratis tengo?", a: "El plan gratuito incluye 5 escaneos de etiquetas al mes. Con Premium tienes uso amplio (hasta 100 escaneos al mes por uso justo)." },
    { q: "¿Cómo escaneo un suplemento?", a: "Entra a \"Escanear\", sube una foto o toma una foto de la etiqueta del suplemento. La IA analiza los ingredientes, su evidencia, carga en órganos y posibles interacciones." },
    { q: "¿Cómo guardo mi stack?", a: "Después de escanear un producto, pulsa \"Guardar en Mi Stack\". Lo verás en la sección \"Mi Stack\" del menú, donde puedes analizar la combinación (Premium)." },
    { q: "¿Cómo edito mi perfil?", a: "Toca tu nombre arriba a la derecha (o \"Perfil\" en el menú móvil). Ahí puedes cambiar tu nombre, idioma y contraseña." },
    { q: "¿Esto es consejo médico?", a: "No. Suppev es una plataforma educativa que resume evidencia científica. No diagnostica, trata, previene ni cura ninguna enfermedad. Consulta siempre a un profesional de la salud antes de tomar suplementos." },
  ],
  en: [
    { q: "How do I cancel my subscription?", a: "You can cancel anytime. Go to the Premium page and tap \"Manage subscription\" to open Stripe's secure portal, where you cancel in one click. You keep Premium access until the end of the period you already paid for." },
    { q: "What does Premium include?", a: "Full PubMed evidence and studies, research by health condition, expert-recommended dosing, the Superathlete Formula, and combined My Stack analysis (kidney/liver load and interactions)." },
    { q: "How much does it cost?", a: "Premium is $9.99 per month or $59.99 per year (save ~50% with the yearly plan). Cancel anytime." },
    { q: "How many free scans do I get?", a: "The free plan includes 5 label scans per month. Premium gives you generous fair use (up to 100 scans per month)." },
    { q: "How do I scan a supplement?", a: "Go to \"Scan\", upload or take a photo of the supplement label. The AI analyzes the ingredients, their evidence, organ load, and possible interactions." },
    { q: "How do I save my stack?", a: "After scanning a product, tap \"Save to My Stack\". You'll find it under \"My Stack\" in the menu, where you can analyze the combination (Premium)." },
    { q: "How do I edit my profile?", a: "Tap your name in the top right (or \"Profile\" in the mobile menu). There you can change your name, language, and password." },
    { q: "Is this medical advice?", a: "No. Suppev is an educational platform that summarizes scientific evidence. It does not diagnose, treat, prevent, or cure any disease. Always consult a healthcare professional before using supplements." },
  ],
};

export const FaqChat = () => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const items = FAQ[lang] || FAQ.en;
  const title = lang === "es" ? "Preguntas frecuentes" : "Frequently asked";
  const greeting = lang === "es" ? "¡Hola! 👋 Elige una pregunta y te respondo al instante." : "Hi! 👋 Pick a question and I'll answer instantly.";
  const backLabel = lang === "es" ? "Volver a las preguntas" : "Back to questions";

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="faq-chat-toggle"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 transition-transform hover:scale-105 hover:bg-emerald-600"
        aria-label={title}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}><X size={24} /></motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}><MessageCircle size={24} /></motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            data-testid="faq-chat-panel"
            className="fixed bottom-24 right-5 z-50 flex max-h-[70vh] w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          >
            <div className="flex items-center gap-2 bg-emerald-500 px-4 py-3.5 text-white">
              <MessageCircle size={18} />
              <p className="text-sm font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-3 rounded-2xl rounded-tl-sm bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{greeting}</div>

              {selected === null ? (
                <div className="space-y-2" data-testid="faq-question-list">
                  {items.map((it, i) => (
                    <button key={i} onClick={() => setSelected(i)} data-testid={`faq-question-${i}`}
                      className="flex w-full items-center justify-between gap-2 rounded-xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50">
                      <span>{it.q}</span>
                      <ChevronRight size={15} className="shrink-0 text-gray-400" />
                    </button>
                  ))}
                </div>
              ) : (
                <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} data-testid="faq-answer">
                  <div className="mb-2 ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white">{items[selected].q}</div>
                  <div className="mb-3 max-w-[92%] rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 text-sm leading-relaxed text-gray-700">{items[selected].a}</div>
                  <button onClick={() => setSelected(null)} data-testid="faq-back-button"
                    className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 hover:text-emerald-700">
                    <ArrowLeft size={13} /> {backLabel}
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FaqChat;
